import { Component, OnInit } from '@angular/core';
import { select, Store} from '@ngrx/store';
import { LineInterfaceState } from '../../store/state/line.state';
import { ConstsHelper } from '../../../../helpers/consts.helper';
import { LineInterface } from '../../models/line.interface';
import { MsisdnProcedureInterfaceState } from '../../store/state/msisdn-procedure.state';
import { LinesService } from '../../services/lines.service';
import * as FileSaver from 'file-saver';
import { ExtranetService } from '../../../shared/services/extranet.service';
import { UpdateCheckPasswordVisibility } from '../../../shared/store/actions/check-password.actions';
import { environment } from '../../../../../environments/environment';
import { OptionInterface } from '../../../shared/models/option.interface';
import { CustomerFilesInterface } from '../../../shared/models/customer-files.interface';

@Component({
  selector: 'app-line-details',
  templateUrl: './line-details.component.html',
  styleUrls: ['./line-details.component.css'],
  standalone: false
})
export class LineDetailsComponent implements OnInit {
  line: LineInterface = null;
  environment = environment;
  filteredOptions: Array<OptionInterface>;
  files: Array<CustomerFilesInterface>;
  displaySupportInformation: boolean;
  displayQrCode: boolean;
  status = {
    label: null,
    details: ''
  };
  FILE_TYPE_SLUG_AMENDMENT = 'amendment';
  FILE_TYPE_SLUG_CONTRACT = 'customers_contract';
  FILE_TYPE_SLUG_RETENTION_CONTRACT = 'retention_contract';
  FILE_TYPE_SLUG_CHANGE_OFFER_RETENTION_CONTRACT = 'retention';
  FILE_TYPE_SUBSCRIPTION_KBIS = 'subscription_kbis';
  brand?: string;

  constructor(
      private linesService: LinesService,
      private store: Store<any>,
      private extranetService: ExtranetService,
  ) {}

  ngOnInit() {
    this.brand = environment.data.brand;
    this.store.pipe(select(ConstsHelper.REDUCER_LINE)).subscribe((response: LineInterfaceState) => {
      if (response.line != null && window.location.href.includes('infos') && (this.line == null || this.line.phoneNumber !== response.line.phoneNumber)) {
        this.line = response.line;
        this.status.details = '';
        this.filteredOptions = [];
        for (const option of this.line.options) {
          if (option.displayWeb === true) {
            this.filteredOptions.push(option);
          }
        }
        this.getLineStatus();
        this.files = [];
        this.linesService.files(
            {
              bucket_slug: environment.data.customerFilesBucketSlug,
              type_slugs: [
                this.FILE_TYPE_SLUG_AMENDMENT,
                this.FILE_TYPE_SLUG_CONTRACT,
                this.FILE_TYPE_SLUG_RETENTION_CONTRACT,
                this.FILE_TYPE_SLUG_CHANGE_OFFER_RETENTION_CONTRACT,
                this.FILE_TYPE_SUBSCRIPTION_KBIS,
              ],
              extra: this.line.phoneNumber
            }
        ).subscribe(
            res => {
              this.files = res;
            }
        );
      }
    });

    this.store.pipe(select(ConstsHelper.REDUCER_MSISDN_PROCEDURES)).subscribe((response: MsisdnProcedureInterfaceState) => {
      if (null !== response.msisdnProcedures) {
        for (const procedure of response.msisdnProcedures) {
          if (procedure.code === ConstsHelper.PROCEDURE_CHANGE_OFFER && procedure.date !== null && procedure.lastMessage !== null) {
            this.status.details += 'Forfait <strong>' + procedure.lastMessage['data']['label_offer'] + '</strong> à partir du <strong>' + procedure.date + '</strong><br>';
          }
          if (procedure.code === ConstsHelper.PROCEDURE_TERMINATE_LINE && procedure.date !== null) {
            this.status.details += 'Résiliation programmée le <strong>' + procedure.date + '</strong><br>';
          }
        }
      }
    });

    this.status.details = '';
    this.files = [];
    this.displayQrCode = false;
    this.getLineStatus();
    this.displaySupportInformation = false;
    if (this.extranetService.getSupportUser()) {
      this.displaySupportInformation = true;
    }
  }

  displayPopup() {
    if (this.extranetService.getSupportUser()) {
      this.store.dispatch(new UpdateCheckPasswordVisibility({visibility: false, isValid: true, password: null, page: 'qr-code'}));
    } else {
      this.store.dispatch(new UpdateCheckPasswordVisibility({visibility: true, isValid: null, password: null, page: 'qr-code'}));
    }
  }

  /**
   * @param typeSlug
   * @param onlyFirstElement
   */
  getFiles(typeSlug: string, onlyFirstElement: boolean): any {
    const files = this.files.find(item => item.typeSlug === typeSlug);
    if (files) {
      if (onlyFirstElement) {
        return files.documents[0];
      } else {
        return files.documents;
      }
    }
    return null;
  }

  downloadFile(id: number, filename: string, typeSlug: string, mimetype: string) {
    this.linesService.downloadFileById(id).subscribe(
        response => {
          FileSaver.saveAs(
              new Blob(
                  [response],
                  {type: mimetype}
              ),
              this.getFileName(filename, typeSlug)
          );
        }
    );
  }

  getFileName(filename: string, typeSlug: string) {
    if (typeSlug === this.FILE_TYPE_SLUG_AMENDMENT) {
      const formattedFile = filename
          .replace(this.line.phoneNumber, '')
          .replace('Amendment', '')
          .replace('_', '');
      const explodeFilename = formattedFile.split('-');
      if (explodeFilename.length === 3) {
        return 'Avenant du ' + explodeFilename[0] + '/' + explodeFilename[1] + '/' + explodeFilename[2];
      }
    }
    return filename;
  }

  private getLineStatus() {
    this.status.label = null;
    if (this.line.statusDetails.portabilityDate && this.line.statusDetails.networkSwap != null && this.line.status === 'Active') {
      this.status.details += 'Changement de réseau prévu le <strong>' + this.formatDate(this.line.statusDetails.portabilityDate) + '</strong><br>';
    }
    if (this.line.statusDetails.portabilityDate && ['waiting_port', 'failed_port'].indexOf(this.line.statusDetails.status) !== -1) {
      this.status.details += 'Portabilité prévue le <strong>' + this.formatDate(this.line.statusDetails.portabilityDate) + '</strong><br>';
    }
    if (this.line.portabilityDate && this.line.statusDetails.status === 'active' && (this.line.statusDetails.endBilling !== null || this.line.statusDetails.retraction !== null)) {
      const portabilityDate = this.formatDate(this.line.portabilityDate, true);
      const endBilling = this.formatDate(this.line.statusDetails.endBilling, true);
      const retraction = this.formatDate(this.line.statusDetails.retraction, true);
      const greatestDate = (endBilling > retraction) ? endBilling : retraction;
      if (portabilityDate >= greatestDate) {
        this.status.details += 'Portabilité prévue le <strong>' + this.formatDate(this.line.portabilityDate) + '</strong><br>';
      }
    }

    if (['Active', 'Suspendue'].indexOf(this.line.status) !== -1 && this.line.statusDetails.endBilling) {
      this.status.details += '<strong>Résiliation programmée le :</strong>' + this.formatDate(this.line.statusDetails.endBilling) + '<br />';
    }

    if (this.line.status === 'Active') {
      this.status.label = '<strong>Ligne activée le : </strong> ' + this.formatDate(this.line.billStartDate) + '<br />';
    }

    if (this.line.status === 'Suspendue' && (this.line.statusDetails.fullSuspension || this.line.statusDetails.partialSuspension)) {
      if (this.line.statusDetails.fullSuspension) {
        this.status.label = '<strong>Ligne suspendue le : </strong>' + this.formatDate(this.line.statusDetails.fullSuspension) + '<br />';
      } else {
        this.status.label = '<strong>Ligne suspendue le : </strong>' + this.formatDate(this.line.statusDetails.partialSuspension) + '<br />';
      }
    }

    if (this.line.status === 'Rétractée' && this.line.statusDetails.retraction) {
      this.status.label = '<strong>Ligne rétractée le : </strong>' + this.formatDate(this.line.statusDetails.retraction) + '<br />';
    }

    if (this.line.status === 'Résiliée' && this.line.statusDetails.endBilling) {
      const endBillingDate = this.formatDate(this.line.statusDetails.endBilling);
      if (endBillingDate !== null) {
        this.status.label = '<strong>Ligne résiliée le : </strong>' + endBillingDate + '<br />';
        const lastBillDate = this.getLastBillDate(endBillingDate);
        if (lastBillDate !== null) {
          this.status.label += 'Dernière facture: ' + lastBillDate + '<br />';
        }
      }
    }

    if (this.line.endCommitmentDate) {
      this.status.details += '<strong>Fin d\'engagement le : </strong>' + this.formatDate(this.line.endCommitmentDate) + '<br />';
    }
  }

  private formatDate(date: any, toNumber?: boolean) {
    if (date == null) {
      return null;
    }
    let explodeDateTime = date.split(' ');
    if (explodeDateTime.length > 0) {
      date = explodeDateTime[0];
    }
    explodeDateTime = date.split('-');
    if (explodeDateTime.length === 3) {
      if (toNumber) {
        date = parseInt(explodeDateTime[0] + explodeDateTime[1] + explodeDateTime[2], 10);
      } else {
        date = explodeDateTime[2] + '/' + explodeDateTime[1] + '/' + explodeDateTime[0];
      }
    }
    return date;
  }

  private getLastBillDate(date: string) {
    const explodeDate = date.split('/');
    if (explodeDate.length === 3) {
      const months = {
        '12': 'janvier',
        '01': 'février',
        '02': 'mars',
        '03': 'avril',
        '04': 'mai',
        '05': 'juin',
        '06': 'juillet',
        '07': 'août',
        '08': 'septembre',
        '09': 'octobre',
        '10': 'novembre',
        '11': 'décembre'
      };
      if (months[explodeDate[1]]) {
        let year = Number(explodeDate[2]);
        if (explodeDate[1] === '12') {
          year += 1;
        }
        return 'début ' + months[explodeDate[1]] + ' ' + year;
      }
    }
    return null;
  }
}
